import React from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import EChartsReact from "echarts-for-react";
import DashboardService from "../../Services/DashboardService";
import { Utils } from "../../Utilities/Utils";
import { EmptyIcon } from "../../Common/FullPageSpinner";

interface IInvoiceCardProps {
  practiceId: number | null;
  fromDate: string;
  toDate: string;
}

const InvoiceCard = ({
  practiceId,
  fromDate,
  toDate,
}: IInvoiceCardProps) => {
  const [chartOptions, setChartOptions] = React.useState<any>({
    xData: [],
    yData: [],
  });
  const [loader, setLoader] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!practiceId) {
      setChartOptions({ xData: [], yData: [] }); // Reset chart data when no selection
      return;
    }
    setLoader(true);
    DashboardService.getInvoiceDetails(practiceId, fromDate, toDate)
      .then((response) => {
        if (response?.status && response?.result) {
          const xData = Object.keys(response?.result);
          const yData = Object.values(response?.result);

          for (var i = 0; i < xData.length; i++) {
            xData[i] = xData[i].charAt(0).toUpperCase() + xData[i].slice(1);
          }
          setChartOptions({ xData, yData }); // Update chart data
        } else {
          setChartOptions({ xData: [], yData: [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching chart data:", error);
        setChartOptions({ xData: [], yData: [] });
      })
      .finally(() => {
        setLoader(false);
      });
  }, [practiceId, fromDate, toDate]);

  return (
    <>
      <Spin
        spinning={loader}
        indicator={<LoadingOutlined style={{ fontSize: 30 }} spin />}
      >
        {chartOptions?.xData?.length > 0 && chartOptions?.yData?.length > 0 ? (
          <EChartsReact
            option={{
              tooltip: {
                trigger: "axis",
                axisPointer: {
                  type: "shadow",
                },
                formatter: function (params: any) {
                  if (Array.isArray(params) && params.length > 0) {
                    return params
                      .map((item: any) => {
                        return `
                        <div>
                          ${item.name}<br/>
                          ${item.seriesName}: 
                          <span style="color:#000; font-weight:600;">
                            £${Utils.getFormattedNumber(item.value)}
                          </span>
                        </div>
                      `;
                      })
                      .join("");
                  }
                  return "";
                },
              },
              toolbox: {
                show: true,
                showTitle: false,
                orient: "vertical",
                right: 4,
                top: 24,
                feature: {
                  // dataView: { show: true, readOnly: false },
                  magicType: { show: true, type: ["line", "bar"] },
                  saveAsImage: { show: true },
                },
              },
              grid: {
                left: "4%",
                right: "5%",
                bottom: "3%",
                containLabel: true,
              },
              xAxis: [
                {
                  type: "category",
                  data: chartOptions?.xData, // Use xData extracted from the response
                  dataTool: {},
                  axisTick: {
                    alignWithLabel: true,
                  },
                  // axisLabel: { interval: 0, rotate: 30 }
                },
              ],
              yAxis: [
                {
                  type: "value",
                  axisLabel: {
                    formatter: function (value: any) {
                      return "£" + Utils.getFormattedNumber(value);
                    },
                  },
                },
              ],
              series: [
                {
                  name: `value`,
                  type: "line",
                  barWidth: "20%",
                  data: chartOptions?.yData,
                  label: {
                    show: false,
                    formatter: function (params: any) {
                      return "£ " + Utils.getFormattedNumber(params.value);
                    },
                  },
                },
              ],
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: 80,
              gap: 8,
            }}
          >
            <EmptyIcon />
          </div>
        )}
      </Spin>
    </>
  );
};

export default InvoiceCard;
